import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { NoticeModalComponent } from '@app/shared/components/notice-modal/notice-modal.component';

/**
 * The bridge that lets the plain print scripts speak through the app's own UI.
 *
 * `assets/scripts/printerCode.js` is loaded as a <script> and has no injector,
 * so it could only ever call alert(). It now calls `window.trakrNotify`, which
 * this service registers once at start-up — so a print failure arrives as a
 * dialog and a non-blocking notice arrives as a toast, like everything else in
 * the app.
 */
@Injectable({ providedIn: 'root' })
export class AppNotifyService {
  private registered = false;

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  /** Called once from the app shell. */
  register(): void {
    if (this.registered || typeof window === 'undefined') {
      return;
    }
    this.registered = true;

    (window as any).trakrNotify = (message: string, kind?: string) => {
      this.show(String(message ?? ''), kind === 'warning' || kind === 'info' ? kind : 'error');
    };
  }

  show(message: string, kind: 'error' | 'warning' | 'info' = 'error'): void {
    if (!message) {
      return;
    }

    /* A warning is something to notice, not something to answer, so it does
       not take the screen. An error stopped the job the operator asked for and
       usually needs reading, so it does. */
    if (kind === 'warning' || kind === 'info') {
      const toast = kind === 'warning' ? this.toastr.warning : this.toastr.info;
      toast.call(this.toastr, message, '', { timeOut: 8000, enableHtml: false });
      return;
    }

    this.dialog.open(NoticeModalComponent, {
      autoFocus: true,
      data: {
        title: this.translate.instant('printLabel'),
        message,
        kind
      }
    });
  }
}
